/**
 * Local-development authentication shim.
 *
 * In production the Istio gateway runs Envoy's ext_authz against the auth
 * service, which validates the Cognito JWT and injects three headers before
 * the request reaches any service:
 *
 *     x-user-id  x-workspace-id  x-user-role
 *
 * Every backend route reads those headers, never the Authorization header. A
 * backend running on localhost has no Envoy in front of it, so those headers
 * are absent and every request is rejected with 401.
 *
 * When REACT_APP_API_BASE points the app at a local backend, this module
 * supplies the same headers from the browser instead. It is inert in every
 * other configuration, so production behaviour is unchanged.
 */

/**
 * Opt-in, and deliberately not inferred from REACT_APP_API_BASE.
 *
 * It used to key off that variable, which conflated two unrelated things:
 * "talk to a different API host" and "there is no Envoy in front of it".
 * Pointing the dashboard at a deployed environment - connect2.weberlo.com,
 * say - is the first, not the second, and would have had the browser send
 * identity headers to a gateway that supplies its own.
 */
export const IS_LOCAL_API = process.env.REACT_APP_LOCAL_AUTH === 'true'

// Captured from the Cognito session on sign-in, so the identity sent to the
// local backend is the real user and matches the rows in a cloned database.
let sessionUserId = ''

export const setSessionUserId = (id: string | undefined) => {
  sessionUserId = id ?? ''
}

/**
 * Identity headers for a local backend.
 *
 * `workspaceId` is deliberately optional and NOT defaulted from the
 * environment. The backend resolves the workspace as
 * `x-workspace-id || query.workspace_id`, so a header always wins — a fixed
 * value here would pin every request to one workspace and silently break
 * switching in the UI. Callers that already know the workspace for a specific
 * request may pass it; everything else relies on the query parameter the API
 * client already sends.
 */
export const localAuthHeaders = (workspaceId?: string): Record<string, string> => {
  if (!IS_LOCAL_API) return {}

  const headers: Record<string, string> = {}

  const userId = process.env.REACT_APP_DEV_USER_ID || sessionUserId
  if (userId) headers['x-user-id'] = userId

  if (workspaceId) headers['x-workspace-id'] = workspaceId

  headers['x-user-role'] = process.env.REACT_APP_DEV_USER_ROLE || 'owner'

  return headers
}
